import { StrictMode, useEffect } from 'react'
import { Switch, Route, useLocation, Router } from 'wouter'
import './index.css'
import { SWRConfig } from 'swr'
import { fetcher } from './lib/fetcher'
import { Toaster } from '@/components/ui/toaster'

import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingProvider } from './components/LoadingProvider'
import NotFound from './components/NotFound'
import { MobileProvider } from './hooks/use-mobile'
import WalletPage from './pages/WalletPage'
import MenuPage from './pages/MenuPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import TransactionsPage from './pages/TransactionsPage'
import WaiverPage from './pages/WaiverPage'
import MembershipPage from './pages/MembershipPage'
import EntryPassesPage from './pages/EntryPassesPage'
import ActiveEntryPassPage from './pages/ActiveEntryPassPage'
import AppKitProvider from './providers/AppKitProvider'
import { UserProvider } from './providers/UserProvider'
import { getFirebaseApp, isRoleEnough, UserRole } from './constants'
import { useUser } from './hooks/use-user'
import AdminCashToTokenPage from './pages/admin/CashToTokenPage'
import AdminTransactionsPage from './pages/admin/TransactionsPage'
import AdminWaiversPage from './pages/admin/WaiversPage'
import AdminVestingManagementPage from './pages/admin/VestingManagementPage'
import AdminUsersPage from './pages/admin/UsersPage'
import AdminEntryPassTemplatesPage from './pages/admin/EntryPassTemplatesPage'
import AdminMembershipTemplatesPage from './pages/admin/MembershipTemplatesPage'
import AuthActionsPage from './pages/AuthActionsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResendVerificationPage from './pages/ResendVerificationPage'
import TermsPage from './pages/TermsPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import { getAuth } from 'firebase/auth'
import BuyTokensPage from './pages/BuyTokensPage.tsx'
import BanxaCheckoutPage from './pages/BanxaCheckoutPage.tsx'
import EventsPage from './pages/EventsPage.tsx'
import NewsPage from './pages/NewsPage.tsx'
import GalleriesPage from './pages/GalleriesPage.tsx'
import ShowEventPage from './pages/events/ShowPage.tsx'
import ShowNewsPage from './pages/news/ShowPage.tsx'
import ShowGalleryPage from './pages/galleries/ShowPage.tsx'
import AccountPage from './pages/AccountPage.tsx'
import ChatPage from './pages/ChatPage.tsx'
import NavigationTabs from './components/common/NavigationTabs.tsx'
import { useNavigationTabs } from './hooks/use-navigation-tabs.ts'
import { PosCartGuardProvider } from './providers/PosCartGuardProvider.tsx'
import { NotificationProvider } from './providers/NotificationProvider.tsx'
import NotificationsPage from './pages/NotificationsPage.tsx'
import CabanasPage from './pages/CabanasPage.tsx'
import CabanaDayPage from './pages/cabanas/DayPage.tsx'
import CabanaNightPage from './pages/cabanas/NightPage.tsx'

// Enhanced navigation guard with improved initial load handling
function withNavigationGuard(
	Component: React.ComponentType<any>,
	requiredRole: UserRole = UserRole.USER
) {
	return function GuardedComponent(props: any) {
		const [location, navigate] = useLocation()

		const user = useUser()

		useEffect(() => {
			if(user.loading) {
				return
			}

			if(user.id && !isRoleEnough(user.role, requiredRole)) {
				navigate('/', { replace: true })
			}

			const auth = getAuth(getFirebaseApp())

			if(!auth?.currentUser?.uid) {
				console.log('--->>>> Redirect back to login from Page guard')
				navigate('/login')
			}
		}, [user, location])

		return (
			<ErrorBoundary>
				<Component {...props} />
			</ErrorBoundary>
		)
	}
}

// Guard all pages
const guardedPages = {
	WalletPage: withNavigationGuard(WalletPage),
	AdminCashToTokenPage: withNavigationGuard(
		AdminCashToTokenPage,
		UserRole.ADMIN
	),
	AdminTransactionsPage: withNavigationGuard(
		AdminTransactionsPage,
		UserRole.ADMIN
	),
	AdminWaiversPage: withNavigationGuard(
		AdminWaiversPage,
		UserRole.ADMIN
	),
	AdminVestingManagement: withNavigationGuard(
		AdminVestingManagementPage,
		UserRole.ADMIN
	),
	AdminUsersPage: withNavigationGuard(
		AdminUsersPage,
		UserRole.ADMIN
	),
	AdminEntryPassTemplatesPage: withNavigationGuard(
		AdminEntryPassTemplatesPage,
		UserRole.ADMIN
	),
	AdminMembershipTemplatesPage: withNavigationGuard(
		AdminMembershipTemplatesPage,
		UserRole.ADMIN
	),
	MenuPage: withNavigationGuard(MenuPage),
	TransactionsPage: withNavigationGuard(TransactionsPage),
	BuyTokensPage: withNavigationGuard(BuyTokensPage),
	WaiverPage: withNavigationGuard(WaiverPage),
	MembershipPage: withNavigationGuard(MembershipPage),
	EntryPassesPage: withNavigationGuard(EntryPassesPage),
	ActiveEntryPassPage: withNavigationGuard(ActiveEntryPassPage),
	BanxaCheckoutPage: withNavigationGuard(BanxaCheckoutPage),
	EventsPage: withNavigationGuard(EventsPage),
	NewsPage: withNavigationGuard(NewsPage),
	GalleriesPage: withNavigationGuard(GalleriesPage),
	ShowEventPage: withNavigationGuard(ShowEventPage),
	ShowNewsPage: withNavigationGuard(ShowNewsPage),
	ShowGalleryPage: withNavigationGuard(ShowGalleryPage),
	AccountPage: withNavigationGuard(AccountPage),
	NotificationsPage: withNavigationGuard(NotificationsPage),
	ChatPage: withNavigationGuard(ChatPage),
	CabanasPage: withNavigationGuard(CabanasPage),
	CabanaDayPage: withNavigationGuard(CabanaDayPage),
	CabanaNightPage: withNavigationGuard(CabanaNightPage)
}

// Enhanced app initialization
const App = () => {
	const { isVisible } = useNavigationTabs()

	return (
	<StrictMode>
		<Router>
			<Switch>
				<Route path="/change-email-confirm">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>
				<Route path="/reset-password">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>
				<Route path="/verify-email">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>

				<Route>
					<ErrorBoundary>
						<AppKitProvider>
							<MobileProvider>
								<LoadingProvider>
									<UserProvider>
									<NotificationProvider>
										<PosCartGuardProvider>
											<SWRConfig
												value={{
													fetcher,
													revalidateOnFocus: false,
													revalidateIfStale: false,
													dedupingInterval: 3000,
													errorRetryCount: 2,
													loadingTimeout: 5000,
													onError: (error) => {
														console.error('SWR Error:', error)
													},
													shouldRetryOnError: false
												}}>
												<Switch>
													<Route path="/" component={guardedPages.WalletPage}/>

													<Route
														path="/admin/cash-to-token"
														component={guardedPages.AdminCashToTokenPage}
													/>
													<Route
														path="/admin/transactions"
														component={guardedPages.AdminTransactionsPage}
													/>
													<Route
														path="/admin/waivers"
														component={guardedPages.AdminWaiversPage}
													/>
													<Route
														path="/admin/vesting"
														component={guardedPages.AdminVestingManagement}
													/>
													<Route
														path="/admin/users"
														component={guardedPages.AdminUsersPage}
													/>
													<Route
														path="/admin/entry-pass-templates"
														component={guardedPages.AdminEntryPassTemplatesPage}
													/>
													<Route
														path="/admin/membership-templates"
														component={guardedPages.AdminMembershipTemplatesPage}
													/>

													<Route path="/menu" component={guardedPages.MenuPage}/>
													<Route path="/login" component={LoginPage}/>
													<Route path="/signup" component={SignupPage}/>
													<Route path="/terms-of-use">
														<TermsPage redirectBackUrl={"/account"} />
													</Route>

													<Route
														path="/privacy-policy"
														component={PrivacyPolicyPage}
													/>
													<Route
														path="/forgot-password"
														component={ForgotPasswordPage}
													/>
													<Route
														path="/resend-verification"
														component={ResendVerificationPage}
													/>
													<Route
														path="/transactions"
														component={guardedPages.TransactionsPage}
													/>
														<Route
														path="/buy-tokens"
														component={guardedPages.BuyTokensPage}
													/>
													<Route
														path="/banxa-checkout"
														component={guardedPages.BanxaCheckoutPage}
													/>
													<Route
														path="/waivers"
														component={guardedPages.WaiverPage}
													/>
													<Route
														path="/membership"
														component={guardedPages.MembershipPage}
													/>
													<Route
														path="/entry-pass"
														component={guardedPages.ActiveEntryPassPage}
													/>
													<Route
														path="/entry-passes"
														component={guardedPages.EntryPassesPage}
													/>
													<Route
														path="/cabanas/day"
														component={guardedPages.CabanaDayPage}
													/>
													<Route
														path="/cabanas/night"
														component={guardedPages.CabanaNightPage}
													/>
													<Route
														path="/cabanas"
														component={guardedPages.CabanasPage}
													/>
													<Route
														path="/events/:id"
														component={guardedPages.ShowEventPage}
													/>
													<Route
														path="/events"
														component={guardedPages.EventsPage}
													/>
													<Route
														path="/news/:id"
														component={guardedPages.ShowNewsPage}
													/>
													<Route
														path="/news"
														component={guardedPages.NewsPage}
													/>
													<Route
														path="/galleries/:id"
														component={guardedPages.ShowGalleryPage}
													/>
													<Route
														path="/galleries"
														component={guardedPages.GalleriesPage}
													/>
													<Route
														path="/account"
														component={guardedPages.AccountPage}
													/>
													<Route
														path="/notifications"
														component={guardedPages.NotificationsPage}
													/>
													<Route
														path="/chat"
														component={guardedPages.ChatPage}
													/>
													<Route component={NotFound}/>
												</Switch>
												{isVisible && <NavigationTabs />}
												<Toaster/>
											</SWRConfig>
										</PosCartGuardProvider>
									</NotificationProvider>
									</UserProvider>
								</LoadingProvider>
							</MobileProvider>
						</AppKitProvider>
					</ErrorBoundary>
				</Route>
			</Switch>
		</Router>
	</StrictMode>
	)
}

export default App